import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
    LogOut, FileText, Users, MessageSquare, Mail, Star, Plus, Trash2, Edit, Save, X, CreditCard, UserCircle, MessagesSquare, Check, Plug, RefreshCw, AlertTriangle, CheckCircle2, ExternalLink, Smartphone, Upload, FlaskConical, TrendingUp
} from "lucide-react";

function Tab({ active, onClick, icon: Icon, label, count, testId }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition w-full text-left ${
                active ? "bg-femcare-pink text-white" : "text-femcare-purple hover:bg-femcare-petal/60"
            }`}
            data-testid={testId}
        >
            <Icon className="w-4 h-4" />
            <span className="flex-1">{label}</span>
            {count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-femcare-pink/15 text-femcare-pink"}`}>
                    {count}
                </span>
            )}
        </button>
    );
}

function StatsView({ stats }) {
    if (!stats) return null;
    const cards = [
        { label: "Total Posts", value: stats.posts, icon: FileText, color: "#E91E8C" },
        { label: "Published", value: stats.published_posts, icon: FileText, color: "#9333A0" },
        { label: "Testimonials", value: stats.testimonials, icon: Star, color: "#EC4899" },
        { label: "Subscribers", value: stats.subscribers, icon: Mail, color: "#F472B6" },
        { label: "Total Messages", value: stats.messages, icon: MessageSquare, color: "#3B0764" },
        { label: "Unread Messages", value: stats.unread_messages, icon: MessageSquare, color: "#E91E8C" },
    ];
    return (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stats-grid">
            {cards.map((c) => (
                <div key={c.label} className="bg-white border border-femcare-border rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${c.color}20` }}>
                            <c.icon className="w-4 h-4" style={{ color: c.color }} />
                        </div>
                        <p className="text-xs uppercase tracking-[0.2em] text-femcare-purple/60">{c.label}</p>
                    </div>
                    <p className="font-serif text-4xl text-femcare-purple">{c.value}</p>
                </div>
            ))}
        </div>
    );
}

function PostForm({ post, onSave, onCancel }) {
    const [form, setForm] = useState(
        post || { title: "", slug: "", excerpt: "", content: "", cover_image: "",
                  category: "Wellness", author: "Femcare Editorial", author_id: null,
                  tags: [], read_time: 5, published: true,
                  title_hi: "", excerpt_hi: "", content_hi: "" }
    );
    const [saving, setSaving] = useState(false);
    const [authors, setAuthors] = useState([]);
    const [tagInput, setTagInput] = useState("");

    useEffect(() => { api.get("/authors").then((r) => setAuthors(r.data)).catch(() => {}); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (post && post.id) {
                const { data } = await api.put(`/admin/posts/${post.id}`, form);
                onSave(data);
            } else {
                const slug = form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                const { data } = await api.post("/admin/posts", { ...form, slug });
                onSave(data);
            }
        } catch (err) {
            alert("Save failed: " + (err.response?.data?.detail || err.message));
        }
        setSaving(false);
    };

    const addTag = (e) => {
        e.preventDefault();
        const tg = tagInput.trim();
        if (tg && !(form.tags || []).includes(tg)) {
            setForm({ ...form, tags: [...(form.tags || []), tg] });
        }
        setTagInput("");
    };
    const removeTag = (tg) => setForm({ ...form, tags: (form.tags || []).filter((t) => t !== tg) });

    return (
        <form onSubmit={submit} className="bg-white border border-femcare-border rounded-2xl p-6 space-y-4" data-testid="post-form">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-serif text-2xl text-femcare-purple">{post && post.id ? "Edit Post" : "New Post"}</h3>
                <button type="button" onClick={onCancel} className="text-femcare-purple/60 hover:text-femcare-pink">
                    <X className="w-5 h-5" />
                </button>
            </div>
            <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" data-testid="post-title-input" />
            <input placeholder="Slug (auto-generated if empty)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" data-testid="post-slug-input" />
            <input required placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" data-testid="post-cover-input" />
            <div className="grid grid-cols-2 gap-4">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" data-testid="post-category-input">
                    {["Cycle Health", "Fertility", "Pregnancy", "Wellness", "Technology", "Community"].map((c) =>
                        <option key={c}>{c}</option>
                    )}
                </select>
                <input type="number" min="1" max="60" placeholder="Read time (min)" value={form.read_time}
                    onChange={(e) => setForm({ ...form, read_time: parseInt(e.target.value) || 5 })}
                    className="bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" data-testid="post-readtime-input" />
            </div>

            {/* Author dropdown */}
            <select value={form.author_id || ""}
                onChange={(e) => {
                    const a = authors.find((x) => x.id === e.target.value);
                    setForm({ ...form, author_id: a?.id || null, author: a?.name || form.author });
                }}
                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" data-testid="post-author-input">
                <option value="">— Select author —</option>
                {authors.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.role}</option>)}
            </select>

            {/* Tags */}
            <div>
                <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-2">Tags</p>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="post-tags">
                    {(form.tags || []).map((tg) => (
                        <span key={tg} className="inline-flex items-center gap-1 text-xs bg-femcare-petal text-femcare-purple px-3 py-1 rounded-full">
                            #{tg}
                            <button type="button" onClick={() => removeTag(tg)} className="text-femcare-pink"><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag(e))}
                        placeholder="Add a tag and press Enter"
                        className="flex-1 bg-femcare-cream border border-femcare-border rounded-xl px-4 py-2"
                        data-testid="post-tag-input" />
                    <button type="button" onClick={addTag} className="px-4 bg-femcare-pink text-white rounded-xl text-sm">Add</button>
                </div>
            </div>

            <textarea required rows={3} placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 resize-none" data-testid="post-excerpt-input" />
            <textarea required rows={10} placeholder="Content (use ## for headings, **bold** for emphasis, blank lines for paragraphs)"
                value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 resize-none font-mono text-sm" data-testid="post-content-input" />

            {/* Hindi optional translations */}
            <details className="bg-femcare-petal/30 border border-femcare-border rounded-xl">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-femcare-purple">हिंदी अनुवाद (optional)</summary>
                <div className="p-4 space-y-3 border-t border-femcare-border">
                    <input placeholder="शीर्षक (Title — Hindi)" value={form.title_hi || ""}
                        onChange={(e) => setForm({ ...form, title_hi: e.target.value })}
                        className="w-full bg-white border border-femcare-border rounded-xl px-4 py-2" data-testid="post-title-hi" />
                    <textarea rows={2} placeholder="संक्षेप (Excerpt — Hindi)" value={form.excerpt_hi || ""}
                        onChange={(e) => setForm({ ...form, excerpt_hi: e.target.value })}
                        className="w-full bg-white border border-femcare-border rounded-xl px-4 py-2 resize-none" data-testid="post-excerpt-hi" />
                    <textarea rows={6} placeholder="लेख सामग्री (Content — Hindi)" value={form.content_hi || ""}
                        onChange={(e) => setForm({ ...form, content_hi: e.target.value })}
                        className="w-full bg-white border border-femcare-border rounded-xl px-4 py-2 resize-none font-mono text-sm" data-testid="post-content-hi" />
                </div>
            </details>

            <label className="flex items-center gap-2 text-sm text-femcare-purple">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                    data-testid="post-published-input" />
                Published
            </label>
            <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="post-save-btn">
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Post"}
            </button>
        </form>
    );
}

function PostsView() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => api.get("/posts?published=false&limit=200").then((r) => setPosts(r.data));
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this post?")) return;
        await api.delete(`/admin/posts/${id}`);
        load();
    };

    if (creating || editing) {
        return <PostForm
            post={editing}
            onSave={() => { setEditing(null); setCreating(false); load(); }}
            onCancel={() => { setEditing(null); setCreating(false); }}
        />;
    }

    return (
        <div data-testid="posts-view">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-femcare-purple">Blog Posts</h2>
                <button onClick={() => setCreating(true)} className="btn-primary" data-testid="new-post-btn">
                    <Plus className="w-4 h-4" /> New Post
                </button>
            </div>
            <div className="bg-white border border-femcare-border rounded-2xl overflow-hidden divide-y divide-femcare-border">
                {posts.map((p) => (
                    <div key={p.id} className="p-5 flex items-center gap-4" data-testid={`admin-post-${p.slug}`}>
                        <img src={p.cover_image} alt={p.title} className="w-16 h-16 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-femcare-purple truncate">{p.title}</p>
                            <p className="text-xs text-femcare-purple/60 mt-1">
                                {p.category} · {p.read_time} min · {p.published ? "Published" : "Draft"}
                            </p>
                        </div>
                        <button onClick={() => setEditing(p)} className="p-2 text-femcare-accent hover:text-femcare-pink" data-testid={`edit-${p.slug}`}>
                            <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(p.id)} className="p-2 text-red-500 hover:text-red-700" data-testid={`delete-${p.slug}`}>
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                {posts.length === 0 && <p className="p-8 text-center text-femcare-purple/60">No posts yet.</p>}
            </div>
        </div>
    );
}

function SubscribersView() {
    const [subs, setSubs] = useState([]);
    const load = () => api.get("/admin/subscribers").then((r) => setSubs(r.data));
    useEffect(() => { load(); }, []);

    return (
        <div data-testid="subscribers-view">
            <h2 className="font-serif text-3xl text-femcare-purple mb-6">Newsletter Subscribers</h2>
            <div className="bg-white border border-femcare-border rounded-2xl overflow-hidden divide-y divide-femcare-border">
                {subs.map((s) => (
                    <div key={s.id} className="p-4 flex items-center justify-between" data-testid={`sub-${s.id}`}>
                        <div>
                            <p className="font-medium text-femcare-purple">{s.email}</p>
                            <p className="text-xs text-femcare-purple/60">{new Date(s.created_at).toLocaleString()}</p>
                        </div>
                        <button
                            onClick={async () => { await api.delete(`/admin/subscribers/${s.id}`); load(); }}
                            className="p-2 text-red-500 hover:text-red-700"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                {subs.length === 0 && <p className="p-8 text-center text-femcare-purple/60">No subscribers yet.</p>}
            </div>
        </div>
    );
}

function MessagesView() {
    const [msgs, setMsgs] = useState([]);
    const load = () => api.get("/admin/messages").then((r) => setMsgs(r.data));
    useEffect(() => { load(); }, []);

    const markRead = async (id) => { await api.post(`/admin/messages/${id}/read`); load(); };
    const remove = async (id) => { await api.delete(`/admin/messages/${id}`); load(); };

    return (
        <div data-testid="messages-view">
            <h2 className="font-serif text-3xl text-femcare-purple mb-6">Contact Messages</h2>
            <div className="space-y-3">
                {msgs.map((m) => (
                    <div
                        key={m.id}
                        className={`bg-white border rounded-2xl p-5 ${m.read ? 'border-femcare-border' : 'border-femcare-pink shadow-sm'}`}
                        data-testid={`msg-${m.id}`}
                    >
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <p className="font-medium text-femcare-purple">{m.name} <span className="text-femcare-purple/50 text-sm">· {m.email}</span></p>
                                <p className="font-serif text-lg text-femcare-purple mt-1">{m.subject}</p>
                            </div>
                            <div className="flex gap-2">
                                {!m.read && (
                                    <button onClick={() => markRead(m.id)} className="text-xs text-femcare-accent hover:text-femcare-pink px-3 py-1 border border-femcare-border rounded-full">
                                        Mark read
                                    </button>
                                )}
                                <button onClick={() => remove(m.id)} className="p-2 text-red-500 hover:text-red-700">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <p className="text-femcare-purple/80 leading-relaxed text-sm mt-3">{m.message}</p>
                        <p className="text-xs text-femcare-purple/50 mt-3">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                ))}
                {msgs.length === 0 && <p className="p-8 text-center text-femcare-purple/60">No messages yet.</p>}
            </div>
        </div>
    );
}

function TestimonialsView() {
    const [items, setItems] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", location: "", quote: "", rating: 5, avatar: "" });

    const load = () => api.get("/testimonials").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        await api.post("/admin/testimonials", form);
        setForm({ name: "", location: "", quote: "", rating: 5, avatar: "" });
        setCreating(false);
        load();
    };

    const remove = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };

    return (
        <div data-testid="testimonials-view">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-femcare-purple">Testimonials</h2>
                <button onClick={() => setCreating(!creating)} className="btn-primary">
                    <Plus className="w-4 h-4" /> {creating ? "Cancel" : "Add"}
                </button>
            </div>
            {creating && (
                <form onSubmit={save} className="bg-white border border-femcare-border rounded-2xl p-6 space-y-3 mb-6">
                    <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <input required placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                        className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <input placeholder="Avatar URL" value={form.avatar} onChange={(e) => setForm({ ...form, avatar: e.target.value })}
                        className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <textarea required rows={3} placeholder="Quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })}
                        className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 resize-none" />
                    <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Save</button>
                </form>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((t) => (
                    <div key={t.id} className="bg-white border border-femcare-border rounded-2xl p-5 relative">
                        <button onClick={() => remove(t.id)} className="absolute top-3 right-3 p-1 text-red-500">
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <p className="font-medium text-femcare-purple">{t.name}</p>
                        <p className="text-xs text-femcare-purple/60 mb-2">{t.location}</p>
                        <p className="text-sm text-femcare-purple/80 italic">"{t.quote}"</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function OrdersView() {
    const [orders, setOrders] = useState([]);
    useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);

    return (
        <div data-testid="orders-view">
            <h2 className="font-serif text-3xl text-femcare-purple mb-6">Orders</h2>
            <div className="bg-white border border-femcare-border rounded-2xl overflow-hidden divide-y divide-femcare-border">
                {orders.map((o) => (
                    <div key={o.id} className="p-5 grid grid-cols-12 gap-4 items-center text-sm" data-testid={`order-${o.id}`}>
                        <div className="col-span-4 min-w-0">
                            <p className="font-mono text-xs text-femcare-purple/60 truncate">{o.receipt}</p>
                            <p className="font-medium text-femcare-purple mt-1">{o.customer_name || "—"}</p>
                            <p className="text-xs text-femcare-purple/60">{o.customer_email || "—"}</p>
                        </div>
                        <div className="col-span-4">
                            {(o.items || []).map((i, k) => (
                                <p key={k} className="text-xs text-femcare-purple/70">{i.name} · ₹{i.price}/{i.period}</p>
                            ))}
                        </div>
                        <div className="col-span-2 text-right">
                            <p className="font-serif text-xl text-femcare-purple">₹{o.amount_inr}</p>
                        </div>
                        <div className="col-span-2 text-right">
                            <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-full ${
                                o.status === "paid" ? "bg-femcare-pink/15 text-femcare-pink" :
                                o.status === "created" ? "bg-femcare-muted text-femcare-purple/60" :
                                "bg-red-100 text-red-600"
                            }`}>{o.status}</span>
                            <p className="text-xs text-femcare-purple/50 mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                ))}
                {orders.length === 0 && <p className="p-8 text-center text-femcare-purple/60">No orders yet.</p>}
            </div>
        </div>
    );
}

function AuthorsView() {
    const [authors, setAuthors] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const blank = { name: "", role: "Editor", bio: "", avatar: "", email: "" };
    const [form, setForm] = useState(blank);

    const load = () => api.get("/authors").then((r) => setAuthors(r.data));
    useEffect(() => { load(); }, []);

    const start = (a) => {
        setEditing(a); setCreating(false);
        setForm(a ? { name: a.name, role: a.role, bio: a.bio, avatar: a.avatar, email: a.email || "" } : blank);
    };
    const startNew = () => { setEditing(null); setCreating(true); setForm(blank); };
    const cancel = () => { setEditing(null); setCreating(false); setForm(blank); };

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form, email: form.email || null };
        if (editing) await api.put(`/admin/authors/${editing.id}`, payload);
        else await api.post("/admin/authors", payload);
        cancel(); load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this author?")) return;
        await api.delete(`/admin/authors/${id}`); load();
    };

    return (
        <div data-testid="authors-view">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-femcare-purple">Authors</h2>
                {!creating && !editing && (
                    <button onClick={startNew} className="btn-primary" data-testid="new-author-btn">
                        <Plus className="w-4 h-4" /> New Author
                    </button>
                )}
            </div>

            {(creating || editing) && (
                <form onSubmit={save} className="bg-white border border-femcare-border rounded-2xl p-6 space-y-4 mb-6" data-testid="author-form">
                    <div className="flex justify-between items-center">
                        <h3 className="font-serif text-xl text-femcare-purple">{editing ? "Edit Author" : "New Author"}</h3>
                        <button type="button" onClick={cancel} className="text-femcare-purple/60 hover:text-femcare-pink"><X className="w-5 h-5" /></button>
                    </div>
                    <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <input required placeholder="Role / title" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <input placeholder="Avatar URL" value={form.avatar} onChange={(e) => setForm({ ...form, avatar: e.target.value })} className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3" />
                    <textarea rows={3} placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 resize-none" />
                    <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Save</button>
                </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {authors.map((a) => (
                    <div key={a.id} className="bg-white border border-femcare-border rounded-2xl p-5 flex gap-4" data-testid={`author-${a.id}`}>
                        {a.avatar && <img src={a.avatar} alt={a.name} className="w-14 h-14 rounded-full object-cover" />}
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-femcare-purple">{a.name}</p>
                            <p className="text-xs text-femcare-accent mb-1">{a.role}</p>
                            <p className="text-xs text-femcare-purple/60 line-clamp-2">{a.bio}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => start(a)} className="p-2 text-femcare-accent hover:text-femcare-pink"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => remove(a.id)} className="p-2 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CommentsView() {
    const [comments, setComments] = useState([]);
    const [filter, setFilter] = useState("pending");

    const load = () => api.get(`/admin/comments?status_filter=${filter}`).then((r) => setComments(r.data));
    useEffect(() => { load(); }, [filter]);

    const approve = async (id) => { await api.post(`/admin/comments/${id}/approve`); load(); };
    const remove = async (id) => { await api.delete(`/admin/comments/${id}`); load(); };

    return (
        <div data-testid="comments-view">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-3xl text-femcare-purple">Comments</h2>
                <div className="inline-flex bg-white border border-femcare-border rounded-full p-1">
                    {["pending", "approved", "all"].map((f) => (
                        <button
                            key={f} onClick={() => setFilter(f === "all" ? "" : f)}
                            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider ${(filter || "all") === f ? "bg-femcare-pink text-white" : "text-femcare-purple/60"}`}
                            data-testid={`comment-filter-${f}`}
                        >{f}</button>
                    ))}
                </div>
            </div>
            <div className="space-y-3">
                {comments.map((c) => (
                    <div key={c.id} className={`bg-white border rounded-2xl p-5 ${c.approved ? 'border-femcare-border' : 'border-femcare-pink shadow-sm'}`} data-testid={`admin-comment-${c.id}`}>
                        <div className="flex justify-between items-start gap-4 mb-3">
                            <div>
                                <p className="font-medium text-femcare-purple">{c.name} <span className="text-femcare-purple/50 text-xs">· {c.email}</span></p>
                                <p className="text-xs text-femcare-accent">on /{c.post_slug}</p>
                            </div>
                            <div className="flex gap-2">
                                {!c.approved && (
                                    <button onClick={() => approve(c.id)} className="text-xs uppercase tracking-wider text-femcare-pink border border-femcare-pink rounded-full px-3 py-1 hover:bg-femcare-pink hover:text-white transition" data-testid={`approve-${c.id}`}>
                                        <Check className="w-3 h-3 inline mr-1" /> Approve
                                    </button>
                                )}
                                <button onClick={() => remove(c.id)} className="p-2 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                            </div>
                        </div>
                        <p className="text-femcare-purple/80 leading-relaxed text-sm whitespace-pre-line">{c.content}</p>
                        <p className="text-xs text-femcare-purple/50 mt-3">{new Date(c.created_at).toLocaleString()}</p>
                    </div>
                ))}
                {comments.length === 0 && <p className="p-8 text-center text-femcare-purple/60">No comments here.</p>}
            </div>
        </div>
    );
}

function IntegrationsView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        setLoading(true);
        api.get("/admin/integrations/status")
            .then((r) => setData(r.data))
            .catch((e) => setData({ error: e?.response?.data?.detail || e.message }))
            .finally(() => setLoading(false));
    };

    useEffect(() => { refresh(); }, []);

    if (loading) return <p className="text-femcare-purple/60">Checking integrations…</p>;
    if (data?.error) return <p className="text-red-600">{data.error}</p>;

    const r = data.resend, rzp = data.razorpay;
    const StatusPill = ({ ok, label }) => (
        <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-medium px-3 py-1 rounded-full ${
            ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"
        }`}>
            {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {label}
        </span>
    );

    return (
        <div className="space-y-6" data-testid="integrations-view">
            <div className="flex justify-between items-center">
                <h2 className="font-serif text-3xl text-femcare-purple">Integrations</h2>
                <button onClick={refresh} className="btn-secondary text-sm" data-testid="refresh-integrations">
                    <RefreshCw className="w-4 h-4" /> Re-check
                </button>
            </div>

            {/* RESEND CARD */}
            <div className="bg-white border border-femcare-border rounded-2xl p-6" data-testid="integration-resend">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Mail className="w-5 h-5 text-femcare-pink" />
                            <h3 className="font-serif text-xl text-femcare-purple">Resend · Receipt emails</h3>
                        </div>
                        <p className="text-sm text-femcare-purple/60">Used for post-purchase receipts and welcome emails.</p>
                    </div>
                    <StatusPill ok={r.production_ready} label={r.production_ready ? "Production ready" : "Sandbox mode"} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="bg-femcare-petal/30 rounded-xl p-3">
                        <p className="text-xs uppercase tracking-wider text-femcare-accent mb-1">Sender</p>
                        <p className="font-mono text-femcare-purple break-all">{r.sender}</p>
                    </div>
                    <div className="bg-femcare-petal/30 rounded-xl p-3">
                        <p className="text-xs uppercase tracking-wider text-femcare-accent mb-1">API key</p>
                        <p className="text-femcare-purple">{r.configured ? "Configured" : "Missing — add RESEND_API_KEY"}</p>
                    </div>
                </div>

                <div className="mt-4">
                    <p className="text-xs uppercase tracking-wider text-femcare-accent mb-2">Verified domains</p>
                    {r.domains.length === 0 ? (
                        <p className="text-sm text-femcare-purple/60">No domains added yet.</p>
                    ) : (
                        <ul className="space-y-1.5">
                            {r.domains.map((d) => (
                                <li key={d.name} className="flex items-center gap-2 text-sm">
                                    <span className={`w-2 h-2 rounded-full ${d.status === "verified" ? "bg-emerald-500" : "bg-amber-400"}`} />
                                    <span className="font-mono">{d.name}</span>
                                    <span className="text-xs uppercase text-femcare-purple/50">{d.status} · {d.region}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {!r.production_ready && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
                        <p className="font-medium mb-2">To send receipts to any customer inbox:</p>
                        <ol className="list-decimal list-inside space-y-1 text-amber-800">
                            <li>Open <a href={r.verify_url} target="_blank" rel="noopener" className="underline inline-flex items-center gap-1">Resend → Domains <ExternalLink className="w-3 h-3" /></a></li>
                            <li>Add <code className="bg-amber-100 px-1 rounded">femcare.life</code> and copy the DNS records into GoDaddy</li>
                            <li>Once verified, set <code className="bg-amber-100 px-1 rounded">SENDER_EMAIL=Femcare &lt;receipts@femcare.life&gt;</code> in <code className="bg-amber-100 px-1 rounded">/app/backend/.env</code> and restart the backend</li>
                        </ol>
                    </div>
                )}
                {r.error && <p className="mt-3 text-xs text-red-600">Last error: {r.error}</p>}
            </div>

            {/* RAZORPAY CARD */}
            <div className="bg-white border border-femcare-border rounded-2xl p-6" data-testid="integration-razorpay">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <CreditCard className="w-5 h-5 text-femcare-pink" />
                            <h3 className="font-serif text-xl text-femcare-purple">Razorpay · Payments</h3>
                        </div>
                        <p className="text-sm text-femcare-purple/60">One-time orders work out-of-the-box. Subscriptions need to be enabled.</p>
                    </div>
                    <StatusPill ok={rzp.subscriptions_enabled} label={rzp.subscriptions_enabled ? "Subscriptions on" : "Subscriptions off"} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="bg-femcare-petal/30 rounded-xl p-3">
                        <p className="text-xs uppercase tracking-wider text-femcare-accent mb-1">Key</p>
                        <p className="font-mono text-femcare-purple text-xs break-all">{rzp.key_id}</p>
                    </div>
                    <div className="bg-femcare-petal/30 rounded-xl p-3">
                        <p className="text-xs uppercase tracking-wider text-femcare-accent mb-1">Mode</p>
                        <p className="text-femcare-purple">{rzp.is_test_mode ? "Test mode" : "Live mode"}</p>
                    </div>
                </div>

                {!rzp.subscriptions_enabled && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
                        <p className="font-medium mb-2">Subscriptions (auto-renew) checkout will fail with a 500 until you enable the product:</p>
                        <ol className="list-decimal list-inside space-y-1 text-amber-800">
                            <li>Open <a href={rzp.enable_url} target="_blank" rel="noopener" className="underline inline-flex items-center gap-1">Razorpay Dashboard → Subscriptions <ExternalLink className="w-3 h-3" /></a></li>
                            <li>Click <strong>Get started</strong> / <strong>Enable subscriptions</strong> (test mode is instant; live mode may need KYC)</li>
                            <li>Come back here and hit <strong>Re-check</strong></li>
                        </ol>
                        <p className="mt-2 text-xs text-amber-700">Tip: one-time checkouts (the "One-time" billing toggle on /pricing) work regardless of this.</p>
                    </div>
                )}
                {rzp.error && rzp.subscriptions_enabled === false && (
                    <p className="mt-3 text-xs text-red-600 break-all">Last error: {rzp.error}</p>
                )}
            </div>

            <p className="text-xs text-femcare-purple/40 text-center">
                Last checked: {data.checked_at && new Date(data.checked_at).toLocaleString()}
            </p>
        </div>
    );
}

function AbHeroVideoView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resetting, setResetting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/ab/hero-video/stats");
            setData(r.data);
        } catch (_) { /* ignore */ }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const reset = async () => {
        if (!window.confirm("Wipe all A/B events for the hero-video experiment?")) return;
        setResetting(true);
        try {
            await api.post("/admin/ab/hero-video/reset");
            await load();
        } finally { setResetting(false); }
    };

    const VARIANT_LABELS = {
        a: "A · Sisterhood",
        b: "B · Sisterhood",
        c: "C · Daughter Care",
    };

    if (loading) return <p className="text-femcare-purple/60">Loading experiment…</p>;
    if (!data) return <p className="text-red-600">Couldn&apos;t load A/B stats.</p>;

    const variants = Object.entries(data.variants);
    const winner = variants.reduce((best, [k, v]) => {
        if (v.impressions < 30) return best; // need minimum sample
        if (!best || v.ctr > best.ctr) return { key: k, ...v };
        return best;
    }, null);

    return (
        <div className="space-y-6" data-testid="ab-hero-video-view">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="editorial-headline text-3xl text-femcare-purple mb-1">Hero video A/B</h2>
                    <p className="text-sm text-femcare-purple/70">
                        Conversion = click on the Home hero &ldquo;Get Femcare Premium&rdquo; button.
                        Sample sticky per browser (localStorage). Force a variant in a browser via
                        <code className="mx-1 px-1.5 py-0.5 rounded bg-femcare-petal/50">?v=a|b|c</code>.
                    </p>
                </div>
                <button
                    onClick={reset}
                    disabled={resetting}
                    className="btn-secondary text-sm disabled:opacity-50"
                    data-testid="ab-reset-btn"
                >
                    <RefreshCw className={`w-4 h-4 ${resetting ? "animate-spin" : ""}`} />
                    Reset
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {variants.map(([k, v]) => {
                    const isWinner = winner && winner.key === k;
                    return (
                        <div
                            key={k}
                            className={`card-editorial p-6 ${isWinner ? "ring-2 ring-femcare-pink" : ""}`}
                            data-testid={`ab-variant-${k}`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <p className="font-serif text-lg text-femcare-purple">{VARIANT_LABELS[k]}</p>
                                {isWinner && (
                                    <span className="text-xs bg-femcare-pink text-white rounded-full px-2 py-0.5">
                                        Leading
                                    </span>
                                )}
                            </div>
                            <dl className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-femcare-purple/60">Impressions</dt>
                                    <dd className="font-medium text-femcare-purple" data-testid={`ab-${k}-impressions`}>{v.impressions}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-femcare-purple/60">Conversions</dt>
                                    <dd className="font-medium text-femcare-purple" data-testid={`ab-${k}-conversions`}>{v.conversions}</dd>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-femcare-border">
                                    <dt className="text-femcare-purple/60">CTR</dt>
                                    <dd className="font-serif text-2xl pink-gradient-text" data-testid={`ab-${k}-ctr`}>{v.ctr}%</dd>
                                </div>
                            </dl>
                        </div>
                    );
                })}
            </div>

            <p className="text-xs text-femcare-purple/50">
                Total impressions across variants: <b>{data.total_impressions}</b>. &ldquo;Leading&rdquo;
                badge appears once a variant has at least 30 impressions.
            </p>
        </div>
    );
}

function ApkBuildView() {
    const [info, setInfo] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const refresh = () => {
        api.get("/downloads/apk/info")
            .then((r) => setInfo(r.data))
            .catch(() => setInfo({ available: false }));
    };

    useEffect(() => { refresh(); }, []);

    const upload = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".apk")) {
            setError("Please pick a .apk file.");
            return;
        }
        setError(""); setSuccessMsg(""); setUploading(true); setProgress(0);

        const CHUNK_SIZE = 4 * 1024 * 1024;   // 4 MB — comfortably under most ingress caps
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        const token = localStorage.getItem("femcare_token") || "";
        const backend = process.env.REACT_APP_BACKEND_URL;

        try {
            // 1) Open a chunked-upload session
            const init = await api.post("/admin/apk/init", {
                filename: file.name,
                total_chunks: totalChunks,
                total_bytes: file.size,
            });
            const uploadId = init.data.upload_id;

            // 2) Ship each chunk sequentially. We use fetch (not axios) so we
            //    can send a FormData with just the slice and no huge memory copies.
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const blob = file.slice(start, end);
                const form = new FormData();
                form.append("chunk", blob, `chunk-${i}.bin`);

                const resp = await fetch(
                    `${backend}/api/admin/apk/chunk?upload_id=${encodeURIComponent(uploadId)}&index=${i}`,
                    {
                        method: "POST",
                        headers: { Authorization: `Bearer ${token}` },
                        body: form,
                    }
                );
                if (!resp.ok) {
                    let msg = `Chunk ${i + 1}/${totalChunks} failed (HTTP ${resp.status})`;
                    try { msg = (await resp.json()).detail || msg; } catch { /* not JSON */ }
                    throw new Error(msg);
                }
                setProgress(Math.round(((i + 1) / totalChunks) * 100));
            }

            // 3) Finalize — reassemble on the server, mark as current.
            await api.post("/admin/apk/finalize", { upload_id: uploadId });

            setSuccessMsg("Uploaded successfully. Visitors on /download will now get this build.");
            refresh();
        } catch (err) {
            setError(err.message || err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const deleteBuild = async () => {
        if (!window.confirm("Remove the current APK? /download will show 'No build available yet' until you upload again.")) return;
        try {
            await api.delete("/admin/apk");
            setSuccessMsg("Build removed.");
            refresh();
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not remove build.");
        }
    };

    const sizeMB = info?.size_bytes ? Math.round(info.size_bytes / (1024 * 1024)) : null;
    const uploadedAt = info?.uploaded_at ? new Date(info.uploaded_at).toLocaleString() : null;

    return (
        <div className="space-y-6" data-testid="apk-view">
            <div className="flex justify-between items-center">
                <h2 className="font-serif text-3xl text-femcare-purple">Android app build</h2>
                <button onClick={refresh} className="btn-secondary text-sm" data-testid="refresh-apk">
                    <RefreshCw className="w-4 h-4" /> Reload
                </button>
            </div>

            {/* CURRENT BUILD */}
            <div className="bg-white border border-femcare-border rounded-2xl p-6" data-testid="apk-current">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Smartphone className="w-5 h-5 text-femcare-pink" />
                            <h3 className="font-serif text-xl text-femcare-purple">Current build</h3>
                        </div>
                        <p className="text-sm text-femcare-purple/60">
                            This is what visitors get when they tap the Android button on <code>/download</code>.
                        </p>
                    </div>
                    {info?.available && (
                        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-medium px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Live
                        </span>
                    )}
                </div>
                {info === null ? (
                    <p className="text-sm text-femcare-purple/60">Loading…</p>
                ) : info.available ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <MetaRow label="Filename" value={<code className="text-xs">{info.filename}</code>} />
                        <MetaRow label="Size" value={`${sizeMB} MB`} />
                        <MetaRow label="Uploaded" value={uploadedAt} />
                    </div>
                ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                        No APK uploaded yet. The /download page will show "No build available yet" to visitors.
                    </div>
                )}
                {info?.available && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/apk`}
                            className="btn-secondary text-sm"
                            data-testid="download-current-apk"
                        >
                            <ExternalLink className="w-4 h-4" /> Test download
                        </a>
                        <button onClick={deleteBuild} className="text-sm text-red-600 hover:text-red-700 underline decoration-red-600/30 underline-offset-4 px-3" data-testid="delete-apk-btn">
                            Remove build
                        </button>
                    </div>
                )}
            </div>

            {/* UPLOAD */}
            <div className="bg-white border border-femcare-border rounded-2xl p-6" data-testid="apk-upload-card">
                <div className="flex items-center gap-2 mb-4">
                    <Upload className="w-5 h-5 text-femcare-pink" />
                    <h3 className="font-serif text-xl text-femcare-purple">Upload new build</h3>
                </div>
                <p className="text-sm text-femcare-purple/60 mb-4">
                    Any new APK you upload here immediately replaces the current one — no redeploy needed.
                    The public download URL <code>/api/downloads/apk</code> always serves whatever is uploaded here.
                </p>

                <label className={`block border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition ${
                    uploading ? "border-femcare-pink/40 bg-femcare-petal/40 cursor-not-allowed"
                              : "border-femcare-border hover:border-femcare-pink bg-femcare-cream"
                }`} data-testid="apk-drop-zone">
                    <Upload className="w-8 h-8 text-femcare-pink mx-auto mb-3" />
                    <p className="font-serif text-lg text-femcare-purple mb-1">
                        {uploading ? "Uploading…" : "Drop or click to pick a .apk file"}
                    </p>
                    <p className="text-xs text-femcare-purple/50">
                        Signed Android APK · uploaded in 4 MB chunks · no size limit
                    </p>
                    <input
                        type="file"
                        accept=".apk,application/vnd.android.package-archive"
                        onChange={(e) => upload(e.target.files?.[0])}
                        disabled={uploading}
                        className="hidden"
                        data-testid="apk-file-input"
                    />
                </label>

                {uploading && (
                    <div className="mt-4">
                        <div className="w-full h-2 bg-femcare-petal/60 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-femcare-pink to-femcare-accent transition-all" style={{ width: `${progress}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-femcare-purple/60 text-center">{progress}% uploaded</p>
                    </div>
                )}

                {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="apk-upload-error">
                        {error}
                    </div>
                )}
                {successMsg && !error && (
                    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800" data-testid="apk-upload-success">
                        {successMsg}
                    </div>
                )}
            </div>
        </div>
    );
}

const MetaRow = ({ label, value }) => (
    <div className="bg-femcare-petal/30 rounded-xl p-3">
        <p className="text-xs uppercase tracking-wider text-femcare-accent mb-1">{label}</p>
        <p className="text-femcare-purple break-all">{value}</p>
    </div>
);

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("stats");
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (user && user.role === "admin") {
            api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
        }
    }, [user, tab]);

    if (loading) return <div className="py-32 text-center text-femcare-purple/60">Loading...</div>;
    if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="admin-dashboard">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-2">Admin Panel</p>
                    <h1 className="editorial-headline text-4xl md:text-5xl text-femcare-purple">Welcome back, {user.name}</h1>
                </div>
                <button onClick={handleLogout} className="btn-secondary" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" /> Sign Out
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <aside className="lg:col-span-3">
                    <nav className="bg-white border border-femcare-border rounded-2xl p-3 space-y-1 sticky top-24">
                        <Tab active={tab === "stats"} onClick={() => setTab("stats")} icon={Users} label="Dashboard" testId="tab-stats" />
                        <Tab active={tab === "posts"} onClick={() => setTab("posts")} icon={FileText} label="Posts" count={stats?.posts} testId="tab-posts" />
                        <Tab active={tab === "authors"} onClick={() => setTab("authors")} icon={UserCircle} label="Authors" testId="tab-authors" />
                        <Tab active={tab === "comments"} onClick={() => setTab("comments")} icon={MessagesSquare} label="Comments" testId="tab-comments" />
                        <Tab active={tab === "orders"} onClick={() => setTab("orders")} icon={CreditCard} label="Orders" testId="tab-orders" />
                        <Link to="/admin/conversions" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium hover:bg-femcare-cream/40 text-femcare-plum" data-testid="tab-conversions">
                            <TrendingUp className="w-4 h-4" /> Web Conversions
                        </Link>
                        <Link to="/admin/publisher-links" className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium hover:bg-femcare-cream/40 text-femcare-plum" data-testid="tab-publisher-links">
                            <TrendingUp className="w-4 h-4" /> Publisher Links
                        </Link>
                        <Tab active={tab === "testimonials"} onClick={() => setTab("testimonials")} icon={Star} label="Testimonials" count={stats?.testimonials} testId="tab-testimonials" />
                        <Tab active={tab === "subscribers"} onClick={() => setTab("subscribers")} icon={Mail} label="Subscribers" count={stats?.subscribers} testId="tab-subscribers" />
                        <Tab active={tab === "messages"} onClick={() => setTab("messages")} icon={MessageSquare} label="Messages" count={stats?.unread_messages} testId="tab-messages" />
                        <Tab active={tab === "integrations"} onClick={() => setTab("integrations")} icon={Plug} label="Integrations" testId="tab-integrations" />
                        <Tab active={tab === "apk"} onClick={() => setTab("apk")} icon={Smartphone} label="App build" testId="tab-apk" />
                        <Tab active={tab === "ab-hero"} onClick={() => setTab("ab-hero")} icon={FlaskConical} label="A/B Hero Video" testId="tab-ab-hero" />
                    </nav>
                </aside>

                <main className="lg:col-span-9">
                    {tab === "stats" && <StatsView stats={stats} />}
                    {tab === "posts" && <PostsView />}
                    {tab === "authors" && <AuthorsView />}
                    {tab === "comments" && <CommentsView />}
                    {tab === "orders" && <OrdersView />}
                    {tab === "testimonials" && <TestimonialsView />}
                    {tab === "subscribers" && <SubscribersView />}
                    {tab === "messages" && <MessagesView />}
                    {tab === "integrations" && <IntegrationsView />}
                    {tab === "apk" && <ApkBuildView />}
                    {tab === "ab-hero" && <AbHeroVideoView />}
                </main>
            </div>
        </div>
    );
}
